// Browser-side adapter for the Munos `fetch` builtin.
//
// Owned by the MunosClient (one per instance — see docs/fetch.md
// §Caching: "Each unique URL is fetched once per session"; here a
// session is the client's lifetime). The adapter is injectable so
// tests can drive completions without hitting the network.
//
// Responsibilities:
//
//   1. Scheme validation. HTTPS only, plus http://localhost for
//      local-development workflows.
//   2. In-memory cache keyed by URL. Cache hits still resolve via
//      the microtask queue — the spec mandates async ordering.
//   3. Concurrency cap (default 16): at most N requests in flight.
//   4. Rate cap (default 30/sec): at most N new requests per rolling
//      second window. Overflow queues, up to maxQueueDepth.
//   5. Queue cap (default 256): requests beyond this fail with
//      msg="rate_limited".
//   6. Anonymous fetches only: GET, no cookies, no custom headers,
//      no body. The browser's CORS rules still apply naturally.
//
// Persistent (IndexedDB-backed) cache is out of scope for v1 — see
// docs/fetch.md §Caching "Two cache layers" / "Persistent". The
// in-memory cache here covers cache layer 1; layer 2 follows.

export interface FetchResult {
  // True iff the operation delivered bytes.
  success: boolean;
  // Raw response body, or empty on failure. Never null.
  data: Uint8Array;
  // Diagnostic string for `event fetch_error`. Present when success
  // is false; omitted on success. Suggested values per spec:
  // "scheme_rejected", "rate_limited", "http_<status>", "network_error",
  // "cors_denied", "timeout".
  errorMsg?: string;
}

export interface FetchAdapter {
  // Issue a fetch and resolve when the operation completes. Never
  // rejects — failures resolve with success=false and errorMsg set.
  fetch(url: string): Promise<FetchResult>;
}

export interface DefaultFetchAdapterOptions {
  // Underlying fetch implementation. Defaults to globalThis.fetch.
  // Tests inject a deterministic stand-in here.
  fetchImpl?: typeof globalThis.fetch;
  // Per-spec defaults; operator-configurable. Suggested 16 / 30 / 256.
  concurrencyCap?: number;
  ratePerSecond?: number;
  maxQueueDepth?: number;
  // Injectable clock for deterministic rate-limit tests.
  now?: () => number;
}

const EMPTY_BYTES = new Uint8Array(0);

export class DefaultFetchAdapter implements FetchAdapter {
  private cache = new Map<string, Uint8Array>();
  private inflight = 0;
  private rateTimestamps: number[] = [];
  private queue: { url: string; resolve: (r: FetchResult) => void }[] = [];
  private kickTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly fetchImpl: typeof globalThis.fetch;
  private readonly concurrencyCap: number;
  private readonly ratePerSecond: number;
  private readonly maxQueueDepth: number;
  private readonly now: () => number;

  constructor(opts: DefaultFetchAdapterOptions = {}) {
    this.fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.concurrencyCap = opts.concurrencyCap ?? 16;
    this.ratePerSecond  = opts.ratePerSecond  ?? 30;
    this.maxQueueDepth  = opts.maxQueueDepth  ?? 256;
    this.now            = opts.now            ?? (() => Date.now());
  }

  async fetch(url: string): Promise<FetchResult> {
    // 1. Scheme validation. Failure path still resolves asynchronously
    //    to preserve the "callbacks fire async" guarantee.
    const schemeError = validateUrl(url);
    if (schemeError !== null) {
      await Promise.resolve();
      return { success: false, data: EMPTY_BYTES, errorMsg: schemeError };
    }
    // 2. Cache hit — still async via microtask.
    const cached = this.cache.get(url);
    if (cached !== undefined) {
      await Promise.resolve();
      return { success: true, data: cached };
    }
    // 3. Queue + caps.
    return new Promise<FetchResult>(resolve => {
      if (this.queue.length >= this.maxQueueDepth) {
        resolve({ success: false, data: EMPTY_BYTES, errorMsg: "rate_limited" });
        return;
      }
      this.queue.push({ url, resolve });
      this.kick();
    });
  }

  // Drain the queue as far as concurrency + rate budget allow. Called
  // on each new enqueue and on each completion.
  private kick(): void {
    while (this.queue.length > 0 && this.inflight < this.concurrencyCap) {
      if (!this.takeRateSlot()) break;
      const item = this.queue.shift()!;
      this.inflight++;
      void this.run(item.url, item.resolve);
    }
    // If the rate window is full but queued work remains, schedule a
    // retry. We compute the wakeup time from the oldest in-window
    // timestamp so we don't poll busy. setTimeout is debounced — at
    // most one outstanding timer at a time.
    if (this.queue.length > 0 && this.inflight < this.concurrencyCap && this.kickTimer === null) {
      const oldest = this.rateTimestamps[0];
      const wait = oldest !== undefined
        ? Math.max(1, 1000 - (this.now() - oldest))
        : 50;
      this.kickTimer = setTimeout(() => {
        this.kickTimer = null;
        this.kick();
      }, wait);
    }
  }

  private takeRateSlot(): boolean {
    this.pruneRate();
    if (this.rateTimestamps.length >= this.ratePerSecond) return false;
    this.rateTimestamps.push(this.now());
    return true;
  }

  private pruneRate(): void {
    const cutoff = this.now() - 1000;
    while (this.rateTimestamps.length > 0 && this.rateTimestamps[0]! < cutoff) {
      this.rateTimestamps.shift();
    }
  }

  private async run(url: string, resolve: (r: FetchResult) => void): Promise<void> {
    try {
      const res = await this.fetchImpl(url, {
        method: "GET",
        credentials: "omit",
        redirect: "follow",
      });
      if (!res.ok) {
        resolve({ success: false, data: EMPTY_BYTES, errorMsg: `http_${res.status}` });
        return;
      }
      const buf = await res.arrayBuffer();
      const bytes = new Uint8Array(buf);
      this.cache.set(url, bytes);
      resolve({ success: true, data: bytes });
    } catch (e: unknown) {
      const raw = e instanceof Error ? e.message : String(e);
      // Browsers raise TypeError for both network failures and CORS
      // denials; we differentiate on message text only as a hint
      // (Chrome's TypeError reads "Failed to fetch" for both).
      const isCors = /cors|cross-origin|opaque/i.test(raw);
      resolve({
        success: false,
        data: EMPTY_BYTES,
        errorMsg: isCors ? "cors_denied" : "network_error",
      });
    } finally {
      this.inflight--;
      this.kick();
    }
  }
}

// HTTPS only, plus http://localhost / 127.0.0.1 / [::1] for local-dev
// asset servers. Anything else (data:, file:, blob:, javascript:,
// remote http://...) is rejected before reaching the network.
export function validateUrl(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return "scheme_rejected";
  }
  if (parsed.protocol === "https:") return null;
  if (parsed.protocol === "http:" && isLocalHost(parsed.hostname)) return null;
  return "scheme_rejected";
}

function isLocalHost(host: string): boolean {
  // Strip brackets from IPv6 hostnames in URL form ("[::1]" → "::1").
  const stripped = host.startsWith("[") && host.endsWith("]")
    ? host.slice(1, -1)
    : host;
  return (
    stripped === "localhost" ||
    stripped === "127.0.0.1" ||
    stripped === "::1"
  );
}
